<script lang="ts">
  import { queryClient } from "../../GraphQL/query";
  import { stringSepToNorm } from "../../random";
  import { rx } from "../../rxfsm";

  import DropDownNavItem from "./DropDownNavItem.svelte";
  import UserDropDown from "./UserDropDown.svelte";
  import Icon from "../Icon.svelte";

  import auth from "../../auth";

  const userRoles = auth.claims.pipe(
    rx.filter((val) => val != null),
    rx.pluck("https://hasura.io/jwt/claims"),
    rx.pluck("x-hasura-allowed-roles"),
  );

  export let currentRoutePath = "";
  export let config: any;
  export let logo: string;

  let showMobileMenu = false;
  let pageTitle = "Dashboard";

  $: {
    currentRoutePath
      .split("/")
      .reduce(
        async (accProm: any, cur: string, i, arr) => {
          const acc = await accProm;
          const foundRoute = findRouteByName(acc.routes, cur);
          const formatTitle = foundRoute?.formatTitle; //Either { remove: true } or { replace: "foo" } or { replaceQuery: "query Query($actual: string) {}" }
          const titleAppendage = formatTitle?.remove
            ? ""
            : formatTitle?.replace ?? formatTitle?.replaceQuery
            ? await queryClient({
                query: formatTitle?.replaceQuery,
                variables: { actual: cur },
              }).then(({ data }) => findTitleKey(data))
            : stringSepToNorm(cur ? cur : "dashboard");

          return {
            name: [...acc.name, titleAppendage],
            routes: foundRoute?.tabroutes ?? foundRoute?.subroutes ?? null,
          };
        },
        { name: [], routes: config.routes },
      )
      .then(({ name }) => {
        pageTitle = name.filter((en) => en != "").join(": ");
      });
  }

  const findTitleKey = (o) => {
    //Early return
    if (o.title) {
      return o.title;
    }
    let result, p;
    for (p in o) {
      if (o.hasOwnProperty(p) && typeof o[p] === "object") {
        result = findTitleKey(o[p]);
        if (result) {
          return result;
        }
      }
    }
    return result;
  };

  const findRouteByName = (routes: Array<any>, findName: string) => {
    if (routes.length == 1 && routes[0].name.includes("[")) return routes[0];

    return routes.find(({ name }) => name == findName);
  };

  const toggleMobileMenu = () => {
    showMobileMenu = !showMobileMenu;
  };

  let routes: Array<any>;
  $: routes = config.routes
    .filter((r) => r.onNav != false)
    .map((r) => {
      if (r.subroutes) {
        return {
          name: stringSepToNorm(r.name),
          location: r.location,
          icon: r.icon,
          roles: r.roles,
          subroutes: r.subroutes.map((sr) => {
            return { route: r.name + "/" + sr.name, name: sr.navName };
          }),
        };
      } else if (r.tabroutes) {
        return {
          name: stringSepToNorm(r.name),
          location: r.location,
          roles: r.roles,
          route: r.name + "/" + r.tabroutes[0].name,
          icon: r.icon,
        };
      }

      return { name: r.navName, location: r.location, route: r.name, roles: r.roles };
    });

  const isCurrentPage = (route: string): boolean => {
    return (currentRoutePath && currentRoutePath === route) || (currentRoutePath === "" && route === "index");
  };
</script>

<svelte:head>
  <title>UNIT | {pageTitle}</title>
</svelte:head>

<nav class="bg-secondary-700">
  <div class="lg:px-16 px-4">
    <div class="flex items-center justify-between h-16">
      <div class="flex items-center">
        <a href="/#" class="flex flex-shrink-0 space-x-4">
          <img
            class="h-8"
            src="data:image/svg+xml;utf8,%3Csvg%20width%3D%22100%25%22%20height%3D%22100%25%22%20viewBox%3D%220%200%20153%20152%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%20xml%3Aspace%3D%22preserve%22%20xmlns%3Aserif%3D%22http%3A%2F%2Fwww.serif.com%2F%22%20style%3D%22fill-rule%3Aevenodd%3Bclip-rule%3Aevenodd%3Bstroke-linejoin%3Around%3Bstroke-miterlimit%3A2%3B%22%3E%20%20%20%20%20%3Cg%20transform%3D%22matrix(1%2C0%2C0%2C1%2C-482.197%2C-938.358)%22%3E%20%20%20%20%20%20%20%20%20%3Cg%20transform%3D%22matrix(1%2C0%2C0%2C1%2C-991.854%2C1.47537)%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Logo%22%20transform%3D%22matrix(1%2C0%2C0%2C1%2C21.2598%2C0)%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20x%3D%221452.79%22%20y%3D%22936.882%22%20width%3D%2230.022%22%20height%3D%22114.407%22%20style%3D%22fill%3Awhite%3B%22%2F%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20transform%3D%22matrix(1.00835%2C0%2C0%2C0.979882%2C-12.4474%2C21.3672)%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20x%3D%221489.91%22%20y%3D%22971.688%22%20width%3D%2277.354%22%20height%3D%2279.601%22%20style%3D%22fill%3Awhite%3B%22%2F%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20transform%3D%22matrix(1%2C0%2C0%2C1%2C0%2C-0.796252)%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20x%3D%221452.79%22%20y%3D%221058.86%22%20width%3D%2230.022%22%20height%3D%2230.035%22%20style%3D%22fill%3Awhite%3B%22%2F%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20transform%3D%22matrix(1%2C0%2C0%2C1%2C122.271%2C-121.973)%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20x%3D%221452.79%22%20y%3D%221058.86%22%20width%3D%2230.022%22%20height%3D%2230.035%22%20style%3D%22fill%3Awhite%3B%22%2F%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M1605.02%2C1058.06L1605.08%2C1058.06L1605.08%2C1088.09L1489.91%2C1088.09L1489.91%2C1058.06L1575%2C1058.06L1575%2C971.688L1605.02%2C971.688L1605.02%2C1058.06Z%22%20style%3D%22fill%3Awhite%3B%22%2F%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%3C%2Fg%3E%20%3C%2Fsvg%3E"
            alt="Logo"
          />
          <img class="h-8" src="data:image/svg+xml;utf8,{encodeURIComponent(logo)}" alt="Product Logo" />
        </a>
        <div class="hidden md:block">
          <div class="ml-10 flex items-baseline">
            {#each routes as route}
              {#if !route.icon && route.location != 'userdropdown' && (route['roles'] ? auth.helper.checkRoles($userRoles, route['roles']) : true)}
                {#if route.subroutes}
                  <DropDownNavItem {route} {currentRoutePath} extraClass={'ml-2'} />
                {:else}
                  {#key currentRoutePath}
                    <a
                      href="#{route.route === 'index' ? '' : route.route}"
                      class:currentPage={isCurrentPage(route.route)}
                      class:otherPageLink={!isCurrentPage(route.route)}
                      class="ml-2 anyPageLink text-xs my-auto font-medium"
                    >
                      {route.name ? route.name : route.route
                            .split('-')
                            .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
                            .join(' ')}
                    </a>
                  {/key}
                {/if}
              {/if}
            {/each}
          </div>
        </div>
      </div>
      <div class="flex flex-row space-x-3">
        {#each routes as route}
          {#if route['roles'] ? auth.helper.checkRoles($userRoles, route['roles']) : true}
            {#if route.icon}
              {#if route.subroutes}
                <DropDownNavItem {route} {currentRoutePath} icon={route.icon} />
              {:else}
                <a
                  href="#{route.route === 'index' ? '.' : route.route}"
                  class="p-2 text-secondary-400 rounded-full hover:text-white focus:outline-none focus:text-white"
                >
                  <div class="flex items-center">
                    <Icon icon={route.icon} />
                  </div>
                </a>
              {/if}
            {/if}
          {/if}
        {/each}
        <div class="hidden md:flex">
          <UserDropDown userdropdown={config.userdropdown} routes={routes.filter((r) => r.location == 'userdropdown')} />
        </div>
        <!-- Mobile menu button -->
        <button
          on:click={toggleMobileMenu}
          class="md:hidden inline-flex items-center justify-center p-2 rounded-md
          text-secondary-400 hover:text-white hover:bg-secondary-500
          focus:outline-none focus:bg-secondary-500 focus:text-white"
        >
          {#if showMobileMenu}
            <svg class="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          {:else}
            <svg class="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          {/if}
        </button>
      </div>
    </div>
  </div>

  <!--
    Mobile menu, toggle classes based on menu state.

    Open: "block", closed: "hidden"
  -->
  {#if showMobileMenu}
    <div class="border-b border-secondary-700 md:hidden">
      <div class="px-2 py-3 sm:px-3">
        <div class="mb-3">
          <UserDropDown userdropdown={config.userdropdown} routes={routes.filter((r) => r.location == 'userdropdown')} {currentRoutePath} />
        </div>
        {#each routes as route}
          {#if route['roles'] ? auth.helper.checkRoles($userRoles, route['roles']) : true}
            {#if !route.icon}
              {#if route.subroutes}
                <DropDownNavItem navigateCallback={toggleMobileMenu} inline={true} {route} {currentRoutePath} extraClass="ml-0 w-full" />
              {:else}
                {#key currentRoutePath}
                  <a
                    href="#{route.route === 'index' ? '' : route.route}"
                    on:click={toggleMobileMenu}
                    class:currentPage={isCurrentPage(route.route)}
                    class:otherPageLink={!isCurrentPage(route.route)}
                    class="block px-3 py-2 anyPageLink text-xs my-auto"
                  >
                    {route.name ? route.name : route.route
                          .split('-')
                          .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
                          .join(' ')}
                  </a>
                {/key}
              {/if}
            {/if}
          {/if}
        {/each}
      </div>
    </div>
  {/if}
</nav>

<header class="bg-white dark:bg-gray-800 shadow">
  <div class="lg:px-16 px-4 py-4">
    <h1 class="text-lg leading-6 font-semibold text-gray-900 dark:text-gray-100">{pageTitle}</h1>
  </div>
</header>

<style>
  .currentPage {
    @apply text-white bg-secondary-500;
  }
  .otherPageLink {
    @apply text-secondary-300;
  }
  .otherPageLink:hover {
    @apply text-white bg-secondary-600;
  }
  .anyPageLink {
    @apply px-3 py-2 rounded-md font-medium;
  }
  .anyPageLink:focus {
    @apply outline-none;
  }
</style>
